import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Smartphone, Download, ShieldCheck, Zap, Wifi, ArrowRight, CheckCircle2, Copy, Info } from "lucide-react";

// Static fallback used until an admin uploads a signed APK via the Beta panel.
const STATIC_APP_INFO = {
  version: "1.0.0",
  build: 1,
  updatedAt: "2026-02-12",
  sizeMB: 5.2,
  minAndroid: "5.0",
  // Set to null to hide the row. Fill in when uploads are available.
  playStoreUrl: null,          // e.g. "https://play.google.com/store/apps/details?id=ly.talabya.app"
  apkDirectUrl: null,          // e.g. "https://talabya-libya.uk/downloads/talabya-v1.0.0.apk"
  iosUrl: null,                // App Store URL when iOS ships
};

export default function DownloadApp() {
  const [copied, setCopied] = useState(false);
  const [appInfo, setAppInfo] = useState(STATIC_APP_INFO);
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "https://talabya-libya.uk";

  // Fetch the latest APK metadata uploaded by admin. If present, use it as the
  // primary "download APK" link and version badge. Falls back silently.
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API) return;
    axios.get(`${API}/api/beta/latest-apk`).then(({ data }) => {
      const apk = data?.apk;
      if (!apk) return;
      setAppInfo((prev) => ({
        ...prev,
        version: apk.version || prev.version,
        build: apk.build || prev.build,
        sizeMB: apk.size_bytes ? Number((apk.size_bytes / 1024 / 1024).toFixed(1)) : prev.sizeMB,
        updatedAt: apk.created_at ? apk.created_at.slice(0, 10) : prev.updatedAt,
        apkDirectUrl: `${API}${apk.download_url}`,
      }));
    }).catch(() => { /* silent */ });
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };
  const APP_INFO = appInfo;

  return (
    <div className="min-h-screen bg-[var(--background)] py-8 px-4" data-testid="download-app-page">
      <div className="max-w-2xl mx-auto">
        {/* Back */}
        <Link to="/welcome" className="inline-flex items-center gap-1 text-primary-brand hover:underline text-sm mb-4" data-testid="back-to-welcome">
          <ArrowRight size={14}/> رجوع
        </Link>

        {/* Hero */}
        <div className="bg-white rounded-3xl talabya-shadow-soft p-8 text-center">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-primary-brand to-blue-700 mx-auto flex items-center justify-center mb-4">
            <Smartphone size={40} className="text-white"/>
          </div>
          <h1 className="text-3xl font-black mb-2" style={{fontFamily:"Cairo"}}>حمّل تطبيق طلبية</h1>
          <p className="text-sm text-[var(--text-secondary)] mb-6">
            التطبيق الأصلي على جهازك — أسرع، بإشعارات فورية، ويعمل بدون فتح المتصفح
          </p>

          {/* Google Play (or "coming soon") */}
          {APP_INFO.playStoreUrl ? (
            <a href={APP_INFO.playStoreUrl} target="_blank" rel="noopener noreferrer"
              className="block w-full py-4 px-6 rounded-2xl bg-black text-white font-bold flex items-center justify-center gap-3 hover:opacity-90 mb-3"
              data-testid="btn-play-store">
              <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M3.6 2.4c-.4.3-.6.7-.6 1.2v16.8c0 .5.2.9.6 1.2l9.5-9.6-9.5-9.6zm14.3 9L15.7 9l-3.5 3.5L15.7 16l2.2-2.4c1.1-.6 1.1-1.6 0-2.2zm-4.1-2l-9-9.1 12 6.9-3 2.2zm-9-9.1v18.2l9-9.1-9-9.1zm3 20.3l6.1-3.5-2.2-2.2-3.9 5.7z"/></svg>
              <div className="text-right">
                <div className="text-[10px] opacity-80">حمّله من</div>
                <div className="text-xl font-black" style={{fontFamily:"Cairo"}}>Google Play</div>
              </div>
            </a>
          ) : (
            <div className="w-full py-4 px-6 rounded-2xl bg-gray-200 text-gray-600 font-bold flex items-center justify-center gap-3 mb-3" data-testid="play-store-coming-soon">
              <Info size={20}/>
              <div>
                <div className="text-xs">قريباً على</div>
                <div className="text-lg font-black" style={{fontFamily:"Cairo"}}>Google Play</div>
              </div>
            </div>
          )}

          {/* Direct APK */}
          {APP_INFO.apkDirectUrl && (
            <a href={APP_INFO.apkDirectUrl} download
              className="block w-full py-4 px-6 rounded-2xl bg-emerald-600 text-white font-bold flex items-center justify-center gap-3 hover:bg-emerald-700 mb-3"
              data-testid="btn-apk-direct">
              <Download size={22}/>
              <div className="text-right">
                <div className="text-[10px] opacity-90">تحميل مباشر (APK)</div>
                <div className="text-lg font-black" style={{fontFamily:"Cairo"}}>الإصدار {APP_INFO.version}</div>
              </div>
            </a>
          )}

          {/* iOS App Store */}
          {APP_INFO.iosUrl ? (
            <a href={APP_INFO.iosUrl} target="_blank" rel="noopener noreferrer"
              className="block w-full py-4 px-6 rounded-2xl bg-gray-900 text-white font-bold flex items-center justify-center gap-3 hover:opacity-90 mb-3"
              data-testid="btn-app-store">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              <div className="text-right">
                <div className="text-[10px] opacity-80">حمّله من</div>
                <div className="text-lg font-black" style={{fontFamily:"Cairo"}}>App Store</div>
              </div>
            </a>
          ) : (
            <div className="w-full py-3 px-4 rounded-2xl bg-gray-100 text-gray-500 text-sm flex items-center justify-center gap-2" data-testid="ios-soon">
              <Info size={14}/> نسخة iPhone قريباً
            </div>
          )}
        </div>

        {/* Info stripe */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          <InfoStat label="الإصدار" value={APP_INFO.version}/>
          <InfoStat label="الحجم" value={`${APP_INFO.sizeMB} MB`}/>
          <InfoStat label="أندرويد" value={`${APP_INFO.minAndroid}+`}/>
        </div>

        {/* Why native */}
        <div className="bg-white rounded-2xl talabya-shadow-soft p-6 mt-4">
          <h2 className="text-lg font-black mb-3" style={{fontFamily:"Cairo"}}>لماذا التطبيق أفضل من الموقع؟</h2>
          <ul className="space-y-3 text-sm">
            <Perk icon={<Zap size={16}/>} text="أسرع بـ 3 أضعاف — يفتح فوراً بدون انتظار المتصفح"/>
            <Perk icon={<ShieldCheck size={16}/>} text="إشعارات فورية بالطلبات حتى لو التطبيق مغلق"/>
            <Perk icon={<Wifi size={16}/>} text="يعمل جزئياً بدون إنترنت (يحفظ الطلبات ويرسلها عند الاتصال)"/>
            <Perk icon={<CheckCircle2 size={16}/>} text="أيقونة مباشرة على الشاشة الرئيسية بدون شريط عنوان مزعج"/>
          </ul>
        </div>

        {/* Meanwhile: install as PWA */}
        {!APP_INFO.apkDirectUrl && !APP_INFO.playStoreUrl && (
          <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-2xl p-5 mt-4 text-sm" data-testid="pwa-fallback">
            <div className="font-black mb-2">💡 حل مؤقت — ثبّت النسخة الحالية كتطبيق ويب</div>
            <ol className="list-decimal pr-4 space-y-1 text-[13px]">
              <li>افتح Chrome على هاتفك</li>
              <li>اذهب إلى: <b>talabya-libya.uk</b></li>
              <li>اضغط قائمة Chrome (⋮) → <b>&quot;تثبيت التطبيق&quot;</b> أو &quot;إضافة إلى الشاشة الرئيسية&quot;</li>
              <li>ستظهر أيقونة طلبية على شاشتك الرئيسية مباشرة</li>
            </ol>
          </div>
        )}

        {/* Share */}
        <div className="bg-white rounded-2xl talabya-shadow-soft p-4 mt-4 flex items-center justify-between">
          <div className="text-xs text-[var(--text-secondary)]">شارك الرابط مع أصدقائك</div>
          <button onClick={copyLink} className="text-primary-brand font-bold text-sm flex items-center gap-1 hover:underline" data-testid="btn-copy-link">
            {copied ? <><CheckCircle2 size={14}/> تم النسخ</> : <><Copy size={14}/> نسخ الرابط</>}
          </button>
        </div>

        <div className="text-center text-[10px] text-[var(--text-secondary)] mt-6">
          آخر تحديث: {APP_INFO.updatedAt} · Build {APP_INFO.build}
        </div>
      </div>
    </div>
  );
}

function InfoStat({ label, value }) {
  return (
    <div className="bg-white rounded-xl p-3 text-center talabya-shadow-soft">
      <div className="text-[10px] text-[var(--text-secondary)]">{label}</div>
      <div className="font-black text-sm mt-1" style={{fontFamily:"Cairo"}}>{value}</div>
    </div>
  );
}

function Perk({ icon, text }) {
  return (
    <li className="flex items-start gap-2">
      <span className="w-7 h-7 rounded-lg bg-primary-light text-primary-brand flex items-center justify-center shrink-0">{icon}</span>
      <span className="pt-1">{text}</span>
    </li>
  );
}
